import { ec as EC } from 'elliptic'
import { decode, encode } from 'cbor2'
import { createWebSocketDispatcher, WebSocketDispatcher } from '../Dispatcher'
import { Id } from '../aliases'
import { agentWebSocketUrl, isLocalhost } from '../environment'
import AgentMessageTypes from './agentMessageTypes'
import AgentStatusCodes from './agentStatusCodes'
import { base64ToUint8, uint8ToBase64 } from '../utils'
import { CborMap, deserialize, serialize } from '@mesh/friendly-cbor-serializer'
import {
  NotificationPayload,
  RegisterClientResponseSchema,
  type ResponseHeader,
  ResponseHeaderSchema,
} from './messages'

const MeshinSubsystem = 1
const ClientTypeWeb = 1
const OsTypeMac = 1
const PublicKeyTypeSecp384r1 = 1
const Aes256EncryptionType = 1
const SignatureTypeECDSA = 1
const LocalStorageKey = 'meshin-agent-config1'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PrimarySessionIdKey = '_sessionIdKey'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PrimaryLinkKey = '_linKey'

export type MeshAgent = {
  initialize: () => Promise<void>

  isAuthenticated: () => boolean

  addMeshOutNotificationListener: (
    listener: MeshOutNotificationListener,
  ) => void

  setReauthenticationNeededListener: (listener: () => void) => void

  buildAndSendMessage: (params: {
    payload: CborMap
    messageType: number
    messageId: Id
  }) => Promise<CborMap>
}

interface AgentConfig {
  keyPair?: EC.KeyPair
  linkKey?: CryptoKey
  sessionId?: Uint8Array
  clientId?: Uint8Array
  agentId?: Uint8Array
  authenticated?: boolean
}

interface AgentConfigExport {
  keyPair?: string
  linkKey?: string
  sessionId?: string
  clientId?: string
  agentId?: string
  authenticated?: boolean
}

export interface MeshOutNotification extends NotificationPayload {}
export type MeshOutNotificationListener = (
  notification: MeshOutNotification,
) => void

type MessageToSend = {
  messageId: Id
  payload: Id
}

async function exportAgentConfig(agentConfig: AgentConfig): Promise<void> {
  let config: AgentConfigExport = {
    sessionId: uint8ToBase64(agentConfig.sessionId),
    clientId: uint8ToBase64(agentConfig.clientId),
    agentId: uint8ToBase64(agentConfig.agentId),
    authenticated: agentConfig.authenticated,
  }
  if (agentConfig.keyPair) {
    const exportKey = agentConfig.keyPair.getPrivate('hex')
    config.keyPair = exportKey
  }
  if (agentConfig.linkKey) {
    const exportedLinkKey = new Uint8Array(
      await crypto.subtle.exportKey('raw', agentConfig.linkKey),
    )
    config.linkKey = uint8ToBase64(exportedLinkKey)
  }
  localStorage.setItem(LocalStorageKey, JSON.stringify(config))
}

async function readAgentConfig(): Promise<AgentConfig | null> {
  let primarySessionId = localStorage.getItem(PrimarySessionIdKey) ?? ''
  let primaryLinkKey = localStorage.getItem(PrimaryLinkKey) ?? ''

  if (isLocalhost) {
    localStorage.removeItem(LocalStorageKey)
    localStorage.removeItem(PrimarySessionIdKey)
    localStorage.removeItem(PrimaryLinkKey)
    primarySessionId = 'AagVlHASKclNDjDcthky9uXYpR14M6YykD0rAlZETcM='
    primaryLinkKey = '7PRfmRFlL9W3KtGJC9DYpdOpqKwidSWCGoDeNghiAcw='
  }

  if (!primarySessionId || !primaryLinkKey) return null
  const configRead = localStorage.getItem(LocalStorageKey)

  const createNewConfig = async () => {
    return {
      sessionId: base64ToUint8(primarySessionId),
      linkKey: await importAesKey(base64ToUint8(primaryLinkKey)!),
    } as AgentConfig
  }

  if (!configRead) {
    return await createNewConfig()
  }
  const config = JSON.parse(configRead) as AgentConfigExport

  if (config.sessionId !== primarySessionId) {
    return await createNewConfig()
  }

  const agentConfig: AgentConfig = {
    sessionId: base64ToUint8(config.sessionId),
    clientId: base64ToUint8(config.clientId),
    agentId: base64ToUint8(config.agentId),
    authenticated: config.authenticated,
  }

  if (config.keyPair) {
    const keyPair = new EC('p256').keyFromPrivate(config.keyPair, 'hex')
    agentConfig.keyPair = keyPair
  }
  if (config.linkKey) {
    const linkKey = await importAesKey(base64ToUint8(config.linkKey)!)
    agentConfig.linkKey = linkKey
  }
  return agentConfig
}
export function getDestinationAgentId(): Uint8Array {
  return new Uint8Array(32)
}

export function getSiteId(): Uint8Array {
  return new Uint8Array(32)
}

function generateAgentKeyPair(): EC.KeyPair {
  const ec = new EC('p256')
  const key = ec.genKeyPair()
  return key
}

export function getPublicKeyPEM(key: EC.KeyPair): string {
  const uncompressed = key.getPublic().encode('array', false)
  const header = new Uint8Array([
    0x30,
    0x59,
    0x30,
    0x13,
    0x06,
    0x07,
    0x2a,
    0x86,
    0x48,
    0xce,
    0x3d,
    0x02,
    0x01,
    0x06,
    0x08,
    0x2a,
    0x86,
    0x48,
    0xce,
    0x3d,
    0x03,
    0x01,
    0x07,
    0x03,
    0x42,
    0x00, // BIT STRING header (65 bytes)
  ])

  const fullDER = new Uint8Array(header.length + uncompressed.length)
  fullDER.set(header, 0)
  fullDER.set(uncompressed, header.length)

  // Encode to base64 without spread
  let str = ''
  for (let i = 0; i < fullDER.length; i++) {
    str += String.fromCharCode(fullDER[i])
  }
  const base64 = btoa(str)
  const wrapped = base64.match(/.{1,64}/g)?.join('\n') ?? base64

  return `-----BEGIN PUBLIC KEY-----\n${wrapped}\n-----END PUBLIC KEY-----`
}

async function importAesKey(rawKey: Uint8Array): Promise<CryptoKey> {
  return await crypto.subtle.importKey('raw', rawKey, 'AES-GCM', true, [
    'encrypt',
    'decrypt',
  ])
}

async function buildMessage(
  messageId: Uint8Array,
  messageType: number,
  payloadObject: any,
  signature?: Uint8Array,
  sessionId?: Uint8Array,
  linkKey?: CryptoKey,
): Promise<MessageToSend> {
  let payload = encode(payloadObject)
  if (linkKey) {
    const iv = crypto.getRandomValues(new Uint8Array(12))
    const ciphertextBuffer = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      linkKey,
      payload,
    )
    const ciphertext = new Uint8Array(ciphertextBuffer)
    const combined = new Uint8Array(iv.length + ciphertext.length)
    combined.set(iv, 0)
    combined.set(ciphertext, iv.length)
    payload = combined
  }
  const messageHeader = new Map()
  messageHeader.set(0, getDestinationAgentId())
  messageHeader.set(1, MeshinSubsystem)
  messageHeader.set(2, messageType)
  messageHeader.set(3, messageId)
  if (sessionId) {
    messageHeader.set(4, sessionId)
  }
  if (linkKey) {
    messageHeader.set(5, Aes256EncryptionType)
  }
  if (signature) {
    messageHeader.set(6, SignatureTypeECDSA)
  }

  const message = new Map()
  message.set(0, messageHeader)
  message.set(1, payload)
  if (signature && signature.length > 0) {
    message.set(2, signature)
  }
  return {
    messageId,
    payload: encode(message),
  }
}

async function buildRegisterClientRequest(
  publicKey: string,
): Promise<MessageToSend> {
  const registerPayload = new Map()
  registerPayload.set(0, ClientTypeWeb)
  registerPayload.set(1, OsTypeMac)
  registerPayload.set(2, '0.16.0')
  registerPayload.set(3, publicKey)
  registerPayload.set(4, PublicKeyTypeSecp384r1)
  const messageId = crypto.getRandomValues(new Uint8Array(32))
  return buildMessage(
    messageId,
    AgentMessageTypes.RegisterClientRequestType,
    registerPayload,
  )
}

async function buildAppPingRequest(
  linkKey: CryptoKey,
  sessionId: Uint8Array,
): Promise<MessageToSend> {
  const messageId = crypto.getRandomValues(new Uint8Array(32))
  return buildMessage(
    messageId,
    AgentMessageTypes.AppPingRequestType,
    new Map(),
    undefined,
    sessionId,
    linkKey,
  )
}

function waitForSocketOpen(
  socket: WebSocket,
  onClose?: () => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    socket.onopen = () => {
      resolve()
    }
    socket.onerror = err => {
      console.error('WebSocket error:', err)
      reject(err)
    }
    socket.onclose = () => {
      if (onClose) {
        onClose()
      }
    }
  })
}

function getWebSocketUrl(): string {
  return agentWebSocketUrl
  //return "wss://localtest.mesh.in:8443/api";
}

export function createAgent(): MeshAgent {
  let agentConfig: AgentConfig = {}
  let socket: WebSocket | null = null
  let dispatcher: WebSocketDispatcher | null = null
  const notificationsListeners = new Map<
    number,
    Set<(notification: NotificationPayload) => void>
  >()

  let reauthNeededListener: (() => void) | null = null
  let reauthTimerId: number | null = null

  const setReauthenticationTimer = (seconds: number) => {
    if (reauthTimerId !== null) {
      clearTimeout(reauthTimerId)
    }
    reauthTimerId = window.setTimeout(() => {
      reauthNeededListener?.()
      reauthTimerId = null
    }, seconds * 1000)
  }

  const resetState = async (clearConfig: boolean) => {
    if (clearConfig) {
      agentConfig = {}
      await exportAgentConfig(agentConfig)
    }
    if (dispatcher) {
      dispatcher.close()
      dispatcher = null
    }
    if (socket) {
      socket.close()
      socket = null
    }
  }

  const listenNotifications = async (
    messageType: number,
    data: Map<number, any>,
  ) => {
    const listeners = notificationsListeners.get(messageType)
    if (!listeners?.size) return

    const linkKey = agentConfig.linkKey

    if (!linkKey) return

    const payload = await decryptMessagePayload(data, linkKey)

    switch (messageType) {
      case AgentMessageTypes.MeshOutNotification:
        listeners.forEach(it => it(payload as MeshOutNotification))
        break
      default:
    }
  }

  const decryptMessagePayload = async (
    data: Map<number, any>,
    linkKey?: CryptoKey,
  ): Promise<Map<number, any>> => {
    let payloadData = data.get(1) as Uint8Array | undefined
    if (!payloadData) return new Map<number, any>()

    if (linkKey && payloadData && payloadData.length > 0) {
      const iv = payloadData.slice(0, 12)
      const ciphertext = payloadData.slice(12)
      const decryptedBuffer = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        linkKey,
        ciphertext,
      )
      payloadData = new Uint8Array(decryptedBuffer)
    }

    return decode<Map<number, any>>(payloadData)
  }

  const sendMessageReceiveReply = async (
    message: MessageToSend,
    linkKey?: CryptoKey,
  ): Promise<CborMap> => {
    if (!dispatcher) {
      throw new Error('Dispatcher not initialized')
    }
    let reply = await dispatcher.send<Map<number, any>>(
      message.messageId,
      message.payload,
    )
    const messageHeader = reply.get(0) as Map<number, any>
    const messageType = messageHeader.get(2) as number
    const status = messageHeader.get(7) as number
    const statusMessage = messageHeader.get(8)
    const messageId = messageHeader.get(3)

    const responseHeader: ResponseHeader = {
      messageType,
      messageId,
      status,
      statusMessage,
    }

    const responseMap = new Map<number, any>()

    responseMap.set(0, serialize(ResponseHeaderSchema, responseHeader))

    if (status !== AgentStatusCodes.Success) {
      if (
        status === AgentStatusCodes.InvalidSessionIdNeedToReauthenticate ||
        status === AgentStatusCodes.ReauthenticationNeeded
      ) {
        reauthNeededListener?.()
        await resetState(true)
      }

      return responseMap
    }

    const nextReauthSeconds = messageHeader.get(15) as number | undefined
    if (nextReauthSeconds !== undefined) {
      setReauthenticationTimer(nextReauthSeconds)
    }
    const payload = await decryptMessagePayload(reply, linkKey)

    responseMap.set(1, payload)
    return responseMap
  }

  const pingInterval = setInterval(async () => {
    if (
      socket &&
      socket.readyState === WebSocket.OPEN &&
      agentConfig.linkKey &&
      agentConfig.sessionId
    ) {
      try {
        const message = await buildAppPingRequest(
          agentConfig.linkKey,
          agentConfig.sessionId,
        )
        await sendMessageReceiveReply(message, agentConfig.linkKey)
      } catch (err) {
        console.error('Error sending ping:', err)
        await resetState(true)
      }
    }
  }, 30000)

  window.addEventListener('unload', () => {
    clearInterval(pingInterval)
    resetState(false)
  })

  const agent = {
    initialize: async () => {
      agentConfig = (await readAgentConfig()) || {}
      if (!agentConfig.keyPair) {
        agentConfig.keyPair = generateAgentKeyPair()
      }
      if (!agentConfig.agentId) {
        agentConfig.agentId = getDestinationAgentId()
      }

      let websocketUrl = getWebSocketUrl()
      if (!websocketUrl) {
        throw new Error('WebSocket URL not set')
      }
      if (!socket) {
        socket = new WebSocket(websocketUrl)
        await waitForSocketOpen(socket, async () => {
          await resetState(false)
        })
      }
      if (!dispatcher) {
        dispatcher = createWebSocketDispatcher(socket)
        dispatcher.setNotificationsListener(listenNotifications)
      }

      if (!agentConfig.clientId) {
        const message = await buildRegisterClientRequest(
          getPublicKeyPEM(agentConfig.keyPair),
        )
        const responseMap = await sendMessageReceiveReply(message)
        const response = deserialize(RegisterClientResponseSchema, responseMap)
        agentConfig.clientId = response.payload?.clientId
      }
      agentConfig.authenticated = !!(
        agentConfig.linkKey && agentConfig.sessionId
      )
      await exportAgentConfig(agentConfig)
    },

    buildAndSendMessage: async (params: {
      payload: CborMap
      messageType: number
      messageId: Id
    }): Promise<CborMap> => {
      await agent.initialize()
      if (
        !dispatcher ||
        !agentConfig.linkKey ||
        !agentConfig.sessionId ||
        !agentConfig.authenticated
      ) {
        throw new Error('Agent not initialized')
      }

      const message = await buildMessage(
        params.messageId,
        params.messageType,
        params.payload,
        undefined,
        agentConfig.sessionId!,
        agentConfig.linkKey!,
      )

      const responseCbor = await sendMessageReceiveReply(
        message,
        agentConfig.linkKey!,
      )

      return responseCbor
    },

    isAuthenticated: (): boolean => {
      return !!agentConfig.authenticated && socket !== null
    },

    addMeshOutNotificationListener: (listener: MeshOutNotificationListener) => {
      const listeners =
        notificationsListeners.get(AgentMessageTypes.MeshOutNotification) ||
        new Set<(notification: NotificationPayload) => void>()
      listeners.add(listener)
      notificationsListeners.set(
        AgentMessageTypes.MeshOutNotification,
        listeners,
      )
    },

    setReauthenticationNeededListener: (listener: () => void) => {
      reauthNeededListener = listener
    },
  }
  return agent as MeshAgent
}
